import React, { useState } from "react";
import axios from "axios";
import { Send, CheckCircle2, Loader2 } from "lucide-react";
import { TRADES } from "../mock";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function LeadForm({ defaultTrade = "", source = "website", compact = false }) {
  const [form, setForm] = useState({
    name: "",
    phone: "",
    email: "",
    trade: defaultTrade,
    website: "",
    message: "",
  });
  const [status, setStatus] = useState("idle"); // idle | loading | success | error
  const [error, setError] = useState("");

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!form.name.trim() || !form.phone.trim()) {
      setError("Please add your name and phone.");
      return;
    }
    setStatus("loading");
    try {
      await axios.post(`${API}/leads`, { ...form, source });
      setStatus("success");
      setForm({ name: "", phone: "", email: "", trade: defaultTrade, website: "", message: "" });
    } catch (err) {
      setStatus("error");
      setError("Something went wrong. Please call 832-280-5553 instead.");
    }
  };

  if (status === "success") {
    return (
      <div
        className="rounded-2xl p-8 text-center"
        style={{ background: "var(--ls-surface)", border: "1px solid rgba(242,106,33,0.5)" }}
      >
        <CheckCircle2 className="w-12 h-12 text-orange mx-auto mb-4" />
        <h3 className="font-cond font-extrabold text-2xl text-white uppercase tracking-wide">
          Got it — you're on my list.
        </h3>
        <p className="text-[#b7ab9b] mt-2">
          I'll reach out fast. Need it now? Call{" "}
          <a href="tel:+18322805553" className="text-orange font-semibold">
            832-280-5553
          </a>
          .
        </p>
        <button className="btn-ghost text-sm mt-6" onClick={() => setStatus("idle")}>
          Send another
        </button>
      </div>
    );
  }

  const inputCls =
    "w-full px-4 py-3.5 rounded-[6px] text-white placeholder:text-[#7d7264] outline-none focus:border-[var(--ls-orange)] transition-colors";
  const inputStyle = { background: "#05070b", border: "1px solid var(--ls-border)" };

  return (
    <form
      onSubmit={submit}
      className="rounded-2xl p-6 md:p-8"
      style={{ background: "var(--ls-surface)", border: "1px solid var(--ls-border-strong)" }}
    >
      <div className="grid sm:grid-cols-2 gap-4">
        <div>
          <label className="block text-xs font-bold uppercase tracking-[0.14em] text-[#b7ab9b] mb-2">
            Name *
          </label>
          <input className={inputCls} style={inputStyle} value={form.name} onChange={update("name")} placeholder="Your name" />
        </div>
        <div>
          <label className="block text-xs font-bold uppercase tracking-[0.14em] text-[#b7ab9b] mb-2">
            Phone *
          </label>
          <input className={inputCls} style={inputStyle} value={form.phone} onChange={update("phone")} placeholder="(832) 555-0123" />
        </div>
        <div>
          <label className="block text-xs font-bold uppercase tracking-[0.14em] text-[#b7ab9b] mb-2">
            Email
          </label>
          <input className={inputCls} style={inputStyle} value={form.email} onChange={update("email")} placeholder="you@business.com" />
        </div>
        <div>
          <label className="block text-xs font-bold uppercase tracking-[0.14em] text-[#b7ab9b] mb-2">
            Trade
          </label>
          <select className={inputCls} style={inputStyle} value={form.trade} onChange={update("trade")}>
            <option value="">Select your trade</option>
            {TRADES.map((t) => (
              <option key={t.name} value={t.name}>
                {t.name}
              </option>
            ))}
          </select>
        </div>
        {!compact && (
          <div className="sm:col-span-2">
            <label className="block text-xs font-bold uppercase tracking-[0.14em] text-[#b7ab9b] mb-2">
              Current website (optional)
            </label>
            <input className={inputCls} style={inputStyle} value={form.website} onChange={update("website")} placeholder="yourwebsite.com" />
          </div>
        )}
        <div className="sm:col-span-2">
          <label className="block text-xs font-bold uppercase tracking-[0.14em] text-[#b7ab9b] mb-2">
            What do you need?
          </label>
          <textarea
            className={inputCls}
            style={inputStyle}
            rows={compact ? 2 : 3}
            value={form.message}
            onChange={update("message")}
            placeholder="Tell me about your business and what's slipping through the cracks."
          />
        </div>
      </div>

      {error && <p className="text-red-400 text-sm mt-4">{error}</p>}

      <button type="submit" className="btn-orange text-base mt-6 w-full justify-center" disabled={status === "loading"}>
        {status === "loading" ? (
          <>
            <Loader2 className="w-4 h-4 animate-spin" /> Sending…
          </>
        ) : (
          <>
            Get More Jobs <Send className="w-4 h-4" />
          </>
        )}
      </button>
      <p className="text-xs text-[#9a8f80] mt-3 text-center font-cond tracking-wide">
        No spam. I'll call you back fast — usually same day.
      </p>
    </form>
  );
}
